import { useState, useCallback, useRef, useEffect } from 'react';
import { contentService } from '../services/contentService';
import { API_URL } from '../config';
import { toast } from 'react-toastify';

/**
 * Custom hook for managing document state in the Canvas editor
 * Handles saving, loading, creating, and deleting documents
 */
export const useDocumentEditor = () => {
  const [currentDocument, setCurrentDocument] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canvasTypeId, setCanvasTypeId] = useState(null);
  const [draftStateId, setDraftStateId] = useState(null);
  const editorContentRef = useRef('');

  // Fetch content types and Canvas type ID on mount
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await contentService.fetchContentTypes();
        
        // Handle both array and object with results property
        const typesArray = Array.isArray(types) ? types : (types.results || types);
        
        const canvasType = typesArray.find(t => t.name === 'Canvas');
        
        if (canvasType) {
          setCanvasTypeId(canvasType.id);
        } else {
          console.error('Canvas content type not found. Available types:', typesArray.map(t => t.name));
          toast.error('Canvas content type not found. Please check backend.');
        }
      } catch (error) {
        console.error('Failed to fetch content types:', error);
        toast.error('Failed to load content types');
      }
    };

    fetchTypes();
  }, []);

  // Fetch states to get Draft state ID
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch(`${API_URL}/api/states/`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        if (response.ok) {
          const states = await response.json();
          
          // Handle both array and object with results property
          const statesArray = Array.isArray(states) ? states : (states.results || states);
          
          const draftState = statesArray.find(s => s.name === 'Draft');
          
          if (draftState) {
            setDraftStateId(draftState.id);
          } else {
            console.error('Draft state not found. Available states:', statesArray.map(s => s.name));
          }
        } else {
          console.error('Failed to fetch states, status:', response.status);
        }
      } catch (error) {
        console.error('Failed to fetch states:', error);
      }
    };

    fetchStates();
  }, []);

  // Create a new document
  const createNewDocument = useCallback(() => {
    const timestamp = new Date().toLocaleString();
    setCurrentDocument({
      id: null,
      title: `Canvas ${timestamp}`,
      content: '',
      content_type_id: canvasTypeId,
      state_id: draftStateId
    });
    editorContentRef.current = '';
    setHasUnsavedChanges(false);
    return true;
  }, [canvasTypeId, draftStateId]);

  // Update editor content (call this when editor content changes)
  const updateEditorContent = useCallback((htmlContent) => {
    editorContentRef.current = htmlContent;
    setHasUnsavedChanges(true);
  }, []);

  // Update document title
  const updateTitle = useCallback((newTitle) => {
    setCurrentDocument(prev => prev ? { ...prev, title: newTitle } : null);
    setHasUnsavedChanges(true);
  }, []);

  // Save document (create or update)
  const saveDocument = useCallback(async () => {
    if (!currentDocument) {
      toast.error('No document to save');
      return false;
    }

    if (!canvasTypeId || !draftStateId) {
      const missing = [];
      if (!canvasTypeId) missing.push('Canvas content type');
      if (!draftStateId) missing.push('Draft state');
      toast.error(`${missing.join(' and ')} not loaded. Please refresh the page.`);
      return false;
    }

    setIsSaving(true);

    try {
      const documentData = {
        ...currentDocument,
        content: editorContentRef.current,
        content_type_id: canvasTypeId,
        state_id: draftStateId
      };

      let savedDoc;
      if (currentDocument.id) {
        // Update existing document
        savedDoc = await contentService.updateContent(documentData);
        toast.success('Document saved successfully');
      } else {
        // Create new document
        savedDoc = await contentService.createContent(documentData);
        toast.success('Document created successfully');
      }

      setCurrentDocument({
        id: savedDoc.id,
        title: savedDoc.title,
        content: savedDoc.content,
        content_type_id: savedDoc.content_type?.id || canvasTypeId,
        state_id: savedDoc.state?.id || draftStateId
      });
      setHasUnsavedChanges(false);
      return true;
    } catch (error) {
      console.error('Failed to save document:', error);
      toast.error('Failed to save document');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [currentDocument, canvasTypeId, draftStateId]);

  // Load a document
  const loadDocument = useCallback((doc) => {
    setCurrentDocument({
      id: doc.id,
      title: doc.title,
      content: doc.content || '',
      content_type_id: doc.content_type?.id || canvasTypeId,
      state_id: doc.state?.id || draftStateId
    });
    editorContentRef.current = doc.content || '';
    setHasUnsavedChanges(false);
    return doc.content || '';
  }, [canvasTypeId, draftStateId]);

  // Delete current document
  const deleteDocument = useCallback(async () => {
    if (!currentDocument?.id) {
      toast.error('No document to delete');
      return false;
    }

    try {
      await contentService.deleteContent(currentDocument.id);
      toast.success('Document deleted successfully');
      createNewDocument();
      return true;
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
      return false;
    }
  }, [currentDocument, createNewDocument]);

  // Check if user can create new document (warn about unsaved changes)
  const canCreateNew = useCallback(() => {
    if (hasUnsavedChanges) {
      return window.confirm('You have unsaved changes. Create new document anyway?');
    }
    return true;
  }, [hasUnsavedChanges]);

  // Check if user can open document (warn about unsaved changes)
  const canOpenDocument = useCallback(() => {
    if (hasUnsavedChanges) {
      return window.confirm('You have unsaved changes. Open another document anyway?');
    }
    return true;
  }, [hasUnsavedChanges]);

  return {
    currentDocument,
    hasUnsavedChanges,
    isSaving,
    createNewDocument,
    updateEditorContent,
    updateTitle,
    saveDocument,
    loadDocument,
    deleteDocument,
    canCreateNew,
    canOpenDocument
  };
};

